// Service work photos: up to five per work, each with a note, kept in Smartsheet (the row's comments). The
// worker routes are in apps/worker/src/routes/servicePhotos.ts; the shapes are shared.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ServicePhoto, ServicePhotoSource, ServicePhotosResponse } from '@technohull/shared';
import { api } from '../lib/api.js';

export type { ServicePhoto, ServicePhotosResponse };

const photosKey = (rowId: number) => ['service-photos', rowId] as const;
/** the image itself, streamed through the worker (the Smartsheet link never reaches the browser). */
export const servicePhotoUrl = (rowId: number, id: number) => `/api/service/photos/file?rowId=${rowId}&id=${id}`;

export function useServicePhotos(rowId: number, enabled = true) {
  return useQuery({
    queryKey: photosKey(rowId),
    queryFn: () => api.get<ServicePhotosResponse>(`/api/service/photos?rowId=${rowId}`),
    enabled,
    staleTime: 30_000,
  });
}

// a write changes the row's photos and (upload / delete) its 📎 / 💬 counts on the Service grid
const refresh = (qc: QueryClient, rowId: number, grid = true) => {
  void qc.invalidateQueries({ queryKey: photosKey(rowId) });
  if (grid) void qc.invalidateQueries({ queryKey: ['service'] });
};

export function useUploadServicePhoto() {
  const qc = useQueryClient();
  return useMutation({
    // `type` is the photo's own type when the file doesn't say (a HEIC picked on Windows often doesn't)
    // `source: 'client'` files it with the photos the client sent
    mutationFn: (v: { rowId: number; file: Blob; name: string; type?: string; note?: string; source?: ServicePhotoSource }) =>
      api.upload<{ ok: true; photo: ServicePhoto }>(
        `/api/service/photos?rowId=${v.rowId}&name=${encodeURIComponent(v.name)}${v.note?.trim() ? `&note=${encodeURIComponent(v.note.trim())}` : ''}${v.source === 'client' ? '&from=client' : ''}`,
        v.file,
        v.type || v.file.type || 'application/octet-stream',
      ),
    // show it straight away; the refetch then brings Smartsheet's own view of the row
    onSuccess: (res, v) => {
      qc.setQueryData<ServicePhotosResponse>(photosKey(v.rowId), (prev) =>
        prev && res?.photo && !prev.photos.some((p) => p.id === res.photo.id) ? { ...prev, photos: [...prev.photos, res.photo] } : prev);
    },
    onSettled: (_d, _e, v) => refresh(qc, v.rowId),
  });
}

export function useEditServicePhotoNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number; commentId: number; note: string }) =>
      api.post<{ ok: true; note: string }>('/api/service/photos/note', v),
    onSuccess: (res, v) => {
      qc.setQueryData<ServicePhotosResponse>(photosKey(v.rowId), (prev) =>
        prev ? { ...prev, photos: prev.photos.map((p) => (p.commentId === v.commentId ? { ...p, note: res?.note ?? v.note.trim() } : p)) } : prev);
    },
    onSettled: (_d, _e, v) => refresh(qc, v.rowId, false),
  });
}

export function useDeleteServicePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { rowId: number; id: number }) => api.post<{ ok: true }>('/api/service/photos/delete', v),
    onSuccess: (_d, v) => {
      qc.setQueryData<ServicePhotosResponse>(photosKey(v.rowId), (prev) =>
        prev ? { ...prev, photos: prev.photos.filter((p) => p.id !== v.id) } : prev);
    },
    onSettled: (_d, _e, v) => refresh(qc, v.rowId),
  });
}
